import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  ITBox,
  QboAlert,
  QboButton,
  QboFormGroup,
  QboLabelGroup,
  QboModal,
  QboSpinnerProgress,
  QboTextField,
  QboTypography,
} from '@ui/Components';
import { Grid, IconButton, InputAdornment } from '@mui/material';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { Delete } from '@mui/icons-material';
import useBillingAdjustmentsAndDiscounts from './pageHooks/useBillingAdjustmentsAndDiscounts';
import NoStatusSection from '../SystemStatusPage/NoStatusSection';

function BillingAdjustmentsAndDiscountsModal({ closeModal, showModal, selectedInvoice }) {
  const { t } = useTranslation();
  const {
    adjustmentState,
    addAdjustment,
    removeAdjustment,
    updateAdjustment,
    isLoading,
    errorMessage,
    form,
    resetAll,
    handleSubmit,
  } = useBillingAdjustmentsAndDiscounts({
    showModal,
    selectedInvoice,
    onSuccessRequest: closeModal,
  });

  const period = useMemo(
    () => dayjs(selectedInvoice.draftInvoiceGeneratedAt).format('MMMM YYYY'),
    [selectedInvoice]
  );

  const closeAndReset = () => {
    resetAll();
    closeModal();
  };

  if (showModal && adjustmentState.loading) {
    return <QboSpinnerProgress className="DefaultApplication__not-ready-loading" overlay />;
  }

  return (
    <QboModal
      onClose={closeAndReset}
      open={showModal}
      title={t('invoices_page.billing_adjustments_and_discounts.title')}
      showCloseIcon
      preventOutsideClick>
      <ITBox className="TransferSubscriptionFormPage__wrapper" direction="column">
        {adjustmentState.error ? (
          <NoStatusSection
            forbiddenErrorMessage={{ heading: t('error'), caption: adjustmentState.error }}
          />
        ) : (
          <QboFormGroup
            loadingButton={isLoading}
            onSubmit={handleSubmit}
            withGenerateAndResetButton
            handleOnResetButton={closeAndReset}
            secondButtonLabel="Cancel"
            firstButtonLabel="Submit">
            <QboAlert
              variant="outlined"
              type="info"
              style={{
                border: '1px solid #ebebeb',
                background: '#f3f3f3',
                fontSize: '10px !important',
                marginTop: '20px',
              }}>
              <QboTypography
                noWrap={false}
                style={{
                  fontSize: '13px',
                  color: '#767676',
                  whiteSpace: 'pre-line',
                }}>
                {t('invoices_page.billing_adjustments_and_discounts.information')}
              </QboTypography>
            </QboAlert>

            {errorMessage && (
              <QboAlert
                variant="outlined"
                type="error"
                style={{
                  background: '#fdeded',
                  fontSize: '10px !important',
                }}>
                <p>{t('invoices_page.billing_adjustments_and_discounts.error')}</p>
                <p>{errorMessage}</p>
              </QboAlert>
            )}

            <ITBox
              className="TransferSubresellerPage__information-box"
              direction="column"
              sx={{ marginBottom: '20px' }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <QboLabelGroup
                    title="Partner ID"
                    description={selectedInvoice.resellerId || '-'}
                  />
                </Grid>
                <Grid item xs={6}>
                  <QboLabelGroup
                    title="Partner Name"
                    description={selectedInvoice.resellerName || '-'}
                  />
                </Grid>
              </Grid>
              <QboLabelGroup title="Invoice ID" description={selectedInvoice.id || '-'} />
              <QboLabelGroup title="Period" description={period} />
            </ITBox>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <QboTextField
                  id="supportTicket"
                  placeholder="Support Ticket"
                  label="Support Ticket"
                  fullWidth
                  inputProps={{ maxLength: 225 }}
                  value={form.values.supportTicket}
                  onChange={(e) => {
                    form.change('supportTicket', e.currentTarget.value);
                  }}
                  errorMessage={form.errors.supportTicket}
                />
              </Grid>

              <Grid item xs={6}>
                <QboTextField
                  id="escalationTicket"
                  placeholder="Escalation Ticket"
                  label="Escalation Ticket"
                  fullWidth
                  value={form.values.escalationTicket}
                  onChange={(e) => {
                    form.change('escalationTicket', e.currentTarget.value);
                  }}
                  inputProps={{ maxLength: 225 }}
                />
              </Grid>
            </Grid>
            <QboTextField
              id="description"
              placeholder="Description"
              label="Description"
              fullWidth
              value={form.values.description}
              onChange={(e) => {
                form.change('description', e.currentTarget.value);
              }}
              inputProps={{ maxLength: 225 }}
            />

            <div style={{ marginTop: '2rem' }}>
              <h2>Adjustments</h2>

              {(adjustmentState.current?.billingAdjustments?.data ?? []).map((item, index) => (
                /* eslint-disable react/no-array-index-key */
                <Grid container spacing={2} key={index} style={{ marginTop: 0 }}>
                  <Grid item xs={5}>
                    <QboTextField
                      id={`adjustmentAmount${index}`}
                      placeholder="E.g. 5"
                      label={index === 0 ? 'Adjustment Amount' : null}
                      type="number"
                      startAdornment={
                        <InputAdornment position="start">{selectedInvoice.currency}</InputAdornment>
                      }
                      fullWidth
                      inputProps={{ maxLength: 225, style: { paddingLeft: '4rem' } }}
                      value={item.amount}
                      onChange={(e) => {
                        updateAdjustment(
                          'billingAdjustments',
                          index,
                          'amount',
                          e.currentTarget.value
                        );
                      }}
                    />
                  </Grid>

                  <Grid item xs={5}>
                    <QboTextField
                      id={`adjustmentReason${index}`}
                      placeholder="E.g. penalty"
                      label={index === 0 ? 'Reason' : null}
                      fullWidth
                      inputProps={{ maxLength: 225 }}
                      value={item.reason}
                      onChange={(e) => {
                        updateAdjustment(
                          'billingAdjustments',
                          index,
                          'reason',
                          e.currentTarget.value
                        );
                      }}
                    />
                  </Grid>

                  <Grid item xs={2}>
                    <IconButton
                      style={{ width: 'max-content', marginTop: index === 0 ? '2.5rem' : '.5rem' }}
                      sx={{
                        position: 'relative',
                        right: '0.7rem',
                      }}
                      onClick={() => removeAdjustment('billingAdjustments', index)}>
                      <Delete sx={{ width: '2rem', height: '2rem' }} />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}

              <div
                style={{
                  marginTop: '2rem',
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  gap: '2rem',
                }}>
                <QboButton
                  style={{ width: 'max-content' }}
                  variant="contained"
                  onClick={() => addAdjustment('billingAdjustments')}>
                  Add More Adjustment
                </QboButton>
                <QboTypography>
                  Total adjustment amount:{' '}
                  <strong>{`${adjustmentState.current?.billingAdjustments?.total_amount ?? 0} ${
                    selectedInvoice.currency
                  }`}</strong>
                </QboTypography>
              </div>
            </div>

            <div style={{ marginTop: '2rem' }}>
              <h2>Discounts</h2>

              {(adjustmentState.current?.discounts?.data ?? []).map((item, index) => (
                /* eslint-disable react/no-array-index-key */
                <Grid container spacing={2} key={index} style={{ marginTop: 0 }}>
                  <Grid item xs={5}>
                    <QboTextField
                      id={`discountAmount${index}`}
                      placeholder="E.g. 5"
                      label={index === 0 ? 'Discount Amount' : null}
                      type="number"
                      startAdornment={
                        <InputAdornment position="start">{selectedInvoice.currency}</InputAdornment>
                      }
                      fullWidth
                      inputProps={{ maxLength: 225, style: { paddingLeft: '4rem' } }}
                      value={item.amount}
                      onChange={(e) => {
                        updateAdjustment('discounts', index, 'amount', e.currentTarget.value);
                      }}
                    />
                  </Grid>

                  <Grid item xs={5}>
                    <QboTextField
                      id={`discountReason${index}`}
                      placeholder="E.g. customer loyalty discount"
                      label={index === 0 ? 'Reason' : null}
                      fullWidth
                      inputProps={{ maxLength: 225 }}
                      value={item.reason}
                      onChange={(e) => {
                        updateAdjustment('discounts', index, 'reason', e.currentTarget.value);
                      }}
                    />
                  </Grid>

                  <Grid item xs={2}>
                    <IconButton
                      style={{ width: 'max-content', marginTop: index === 0 ? '2.5rem' : '.5rem' }}
                      sx={{
                        position: 'relative',
                        right: '0.7rem',
                      }}
                      onClick={() => removeAdjustment('discounts', index)}>
                      <Delete sx={{ width: '2rem', height: '2rem' }} />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}

              <div
                style={{
                  marginTop: '2rem',
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  gap: '2rem',
                }}>
                <QboButton
                  style={{ width: 'max-content' }}
                  variant="contained"
                  onClick={() => addAdjustment('discounts')}>
                  Add More Discount
                </QboButton>
                <QboTypography>
                  Total discount amount:{' '}
                  <strong>{`${adjustmentState.current?.discounts?.total_amount ?? 0} ${
                    selectedInvoice.currency
                  }`}</strong>
                </QboTypography>
              </div>
            </div>
          </QboFormGroup>
        )}
      </ITBox>
    </QboModal>
  );
}

BillingAdjustmentsAndDiscountsModal.propTypes = {
  closeModal: PropTypes.func,
  showModal: PropTypes.bool,
  selectedInvoice: PropTypes.object,
};

BillingAdjustmentsAndDiscountsModal.defaultProps = {
  closeModal: () => {},
  showModal: false,
  selectedInvoice: null,
};

export default BillingAdjustmentsAndDiscountsModal;
